import { useMemo, useRef, useEffect, useState } from 'react'
import { LEAGUES_SORT_PRIORITY } from '@/Overtime/constants/sports'
import { type League } from 'overtime-utils'
import useSportsMarketsV2Query from '@/Overtime/queries/useSportsMarketsV2Query'
import { StatusFilter } from '@/Overtime/enums/markets'
import { type SupportedNetwork } from '@/Overtime/types/network'

type Market = {
  leagueId: League
  [key: string]: any
}

type UseLeagueSortedMarketsProps = {
  networkId: SupportedNetwork
  maxGames?: number
  enabled?: boolean
}

export const useLeagueSortedMarkets = ({
  networkId,
  maxGames = 9,
  enabled = true,
}: UseLeagueSortedMarketsProps) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const hasLoadedOnce = useRef(false)
  const previousData = useRef<any>(null)

  const openSportMarketsQuery = useSportsMarketsV2Query(
    StatusFilter.OPEN_MARKETS,
    false,
    { networkId },
    undefined,
    { enabled }
  )

  const openSportMarkets = useMemo(() => {
    if (openSportMarketsQuery.isSuccess && openSportMarketsQuery.data) {
      return openSportMarketsQuery.data[StatusFilter.OPEN_MARKETS]
    }
    return undefined
  }, [openSportMarketsQuery.data, openSportMarketsQuery.isSuccess])

  // Track initial load and data changes
  useEffect(() => {
    if (openSportMarketsQuery.isSuccess && !hasLoadedOnce.current) {
      hasLoadedOnce.current = true
      setIsInitialLoad(false)
      previousData.current = openSportMarkets
    } else if (openSportMarketsQuery.isSuccess && hasLoadedOnce.current) {
      // Check if data has actually changed
      const currentData = JSON.stringify(openSportMarkets)
      const prevData = JSON.stringify(previousData.current)

      if (currentData !== prevData) {
        previousData.current = openSportMarkets
        setIsInitialLoad(false)
      }
    }
  }, [openSportMarketsQuery.isSuccess, openSportMarkets])

  const sortedMarkets = useMemo(() => {
    if (!openSportMarkets) {
      return []
    }

    // Group markets by league
    const marketsByLeague = openSportMarkets.reduce(
      (acc, market) => {
        if (!acc[market.leagueId]) {
          acc[market.leagueId] = []
        }
        acc[market.leagueId].push(market)
        return acc
      },
      {} as Record<League, Market[]>
    )

    // Sort leagues by priority
    const sortedLeagues = LEAGUES_SORT_PRIORITY.filter(
      league => marketsByLeague[league]
    )

    const result: Market[] = []
    let gameCount = 0
    let round = 0 // 0 for first game from each league, 1 for second game, etc.

    while (gameCount < maxGames && round < 10) {
      // Prevent infinite loop
      for (const league of sortedLeagues) {
        if (gameCount >= maxGames) {
          break
        }

        const leagueMarkets = marketsByLeague[league]
        if (leagueMarkets && leagueMarkets[round]) {
          result.push(leagueMarkets[round])
          gameCount++
        }
      }
      round++
    }

    return result
  }, [openSportMarkets, maxGames])

  return {
    sortedMarkets,
    isLoading: isInitialLoad && openSportMarketsQuery.isFetching,
    isError: openSportMarketsQuery.isError,
    error: openSportMarketsQuery.error,
  }
}
